/**
 * Axios client centralizat cu interceptori pentru:
 * - Auto-attach JWT token
 * - Centralized error handling
 * - Token refresh logic
 * - Automatic 401/403 handling
 */

import axios from 'axios';
import Cookies from 'js-cookie';
import logger from '../utils/logger';

const BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:7039';

// Creează instanța axios
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 secunde timeout
});

// Request interceptor - adaugă token automat la fiecare request
apiClient.interceptors.request.use(
  (config) => {
    const token = Cookies.get('jwtToken');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const traceId = sessionStorage.getItem('traceId');
    if (traceId) {
      config.headers['X-Trace-Id'] = traceId;
    }

    logger.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);

    return config;
  },
  (error) => {
    logger.error('[API Request Error]', error);
    return Promise.reject(error);
  },
);

// Response interceptor - gestionează erori și logging
apiClient.interceptors.response.use(
  (response) => {
    logger.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url} ${response.status}`);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    logger.error(`[API Response Error] ${error.config?.url} ${error.response?.status || error.message}`);

    // Handle 401 Unauthorized - Token expirat sau invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Pagini publice care NU trebuie să redirecționeze la login
      const publicPages = [
        '/licitatii/',
        '/achizitii-directe/',
        '/anunturi-intentie/',
        '/anunturi-atribuire/',
        '/proiecte/',
        '/proiecte',
        '/',
        '/wiki',
        '/contact',
        '/abonamente',
      ];

      const isPublicPage = publicPages.some((page) =>
        window.location.pathname.includes(page),
      );

      // Pentru paginile publice, NU face redirect - doar curăță token-ul
      if (isPublicPage) {
        logger.log('[API] 401 pe pagină publică - popup conversie va apărea');
        // Curăță token-ul dar NU redirecționa
        Cookies.remove('jwtToken');
        Cookies.remove('userId');
        Cookies.remove('subscriptionType');
        return Promise.reject(error);
      }

      // Pentru paginile protejate, curăță token-ul și redirecționează la login
      Cookies.remove('jwtToken');
      Cookies.remove('userId');
      Cookies.remove('subscriptionType');

      logger.warn('[API] Token expirat - redirect la login');

      // Redirecționează doar dacă nu suntem deja pe pagina de login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }

      return Promise.reject(error);
    }

    // Handle 403 Forbidden - Acces interzis
    if (error.response?.status === 403) {
      logger.error('[API] Acces interzis - 403 Forbidden');
      // Poți adăuga logică specifică pentru 403
    }

    // Handle 500 Server Errors
    if (error.response?.status >= 500) {
      logger.error('[API] Eroare server - 500+');
      // Poți adăuga notificare pentru utilizator
    }

    return Promise.reject(error);
  },
);

/**
 * Helper function pentru a face request-uri GET cu gestionare erori
 */
export const get = async (url, config = {}) => {
  try {
    const response = await apiClient.get(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function pentru a face request-uri POST cu gestionare erori
 */
export const post = async (url, data = {}, config = {}) => {
  try {
    const response = await apiClient.post(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function pentru a face request-uri PUT cu gestionare erori
 */
export const put = async (url, data = {}, config = {}) => {
  try {
    const response = await apiClient.put(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function pentru a face request-uri DELETE cu gestionare erori
 */
export const del = async (url, config = {}) => {
  try {
    const response = await apiClient.delete(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default apiClient;
